import React, { useEffect, FunctionComponent, useState } from 'react'
import ReactDOM from 'react-dom'
import './styles.scss'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { InteractionToggle } from './interactionToggle'
import browserCheck from '@ta-interaktiv/browsercheck'

interface MapBoxInterface {
  /** Function to receive the Mapbox-Map-Object */
  getMap?: Function
  /** Gets called on map activation */
  onActivation?: Function
  /** Gets called on map deactivation */
  onDeactivation?: Function
  /** ID for the mapboxgl map element */
  id?: string
  /** Mapbox-Style-JSON for the mapboxgl map */
  mapStyle?: string
  /** Coordinates to center the map on */
  center?: mapboxgl.LngLatLike
  /** Inital zoom */
  zoom?: number
  /** Boolean to toggle map activation */
  userActivated?: boolean
  /** Text strings for the activation button */
  locales?: { mobile: string[]; desktop: string[] }
  /** Custom attribution */
  attribution?: string
  /** Map bounds */
  maxBounds?: mapboxgl.LngLatBoundsLike
  /** Map bounds for fullscreen */
  maxBoundsFullscreen?: Partial<mapboxgl.LngLatBoundsLike>
  /** React component to show above fullscreen map */
  FullscreenMapKey: JSX.Element | undefined
  props?: any
  [key: string]: any
}
const baseLocales = {
  mobile: ['Interaktive Karte', 'Zurück zum Artikel'],
  desktop: ['Karte aktivieren', 'Scrollen aktivieren']
}
const popUpOffsetSize = 5
const popUpOptions: mapboxgl.PopupOptions = {
  closeButton: true,
  closeOnClick: false,
  maxWidth: 'none',
  offset: {
    top: [0, popUpOffsetSize],
    'top-left': [0, popUpOffsetSize],
    'top-right': [0, popUpOffsetSize],
    bottom: [0, -popUpOffsetSize],
    'bottom-left': [0, -popUpOffsetSize],
    'bottom-right': [0, -popUpOffsetSize],
    left: [popUpOffsetSize, 0],
    right: [-popUpOffsetSize, 0],
    center: [0, 0]
  }
}
const disableScrolling = () => {
  document.body.style.overflow = 'hidden'
}
const enableScrolling = () => {
  document.body.style.overflow = 'visible'
}
export const MapBox: FunctionComponent<MapBoxInterface> = ({
  getMap,
  onActivation,
  onDeactivation,
  id = 'map',
  center,
  mapStyle,
  maxBounds,
  maxBoundsFullscreen,
  zoom = 10,
  FullscreenMapKey,
  attribution = undefined,
  userActivated = true,
  locales = baseLocales,
  ...props
}) => {
  if (!maxBoundsFullscreen) maxBoundsFullscreen = maxBounds
  // Zoom-Control
  const [isActive, setActive] = useState(userActivated)
  const [isFullscreen, setFullscreen] = useState(false)

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: id,
      style: mapStyle,
      center: center,
      maxBounds: maxBounds,
      zoom: zoom,
      attributionControl: false
    })
    // Add controls
    const navigationControl = new mapboxgl.NavigationControl({
      showCompass: false
    })
    const attributionControl = new mapboxgl.AttributionControl({
      customAttribution: attribution
    })
    map
      .addControl(attributionControl, 'bottom-right')
      .addControl(navigationControl, 'top-right')
    // Add activation/deactivation button
    if (isActive === true) {
      const interactionControl = new InteractionToggle(map, locales)
      map.addControl(interactionControl, 'top-right')
    }

    // Preparation for popup
    map.popup = new mapboxgl.Popup(popUpOptions)
    map.addPopup = (el: JSX.Element, lnglat: mapboxgl.LngLatLike) => {
      const placeholder = document.createElement('div')
      ReactDOM.render(el, placeholder)
      map.popup
        .setDOMContent(placeholder)
        .setLngLat(lnglat)
        .addTo(map)
    }
    map.removePopup = () => {
      map.popup.remove()
    }

    // Activate Interactions for Map
    map.activateMap = () => {
      map.boxZoom.enable()
      map.scrollZoom.enable()
      map.dragPan.enable()
      map.keyboard.enable()
      map.touchZoomRotate.enable()
      map.touchZoomRotate.disableRotation()
      map.doubleClickZoom.enable()

      if (browserCheck.isMobile) {
        const m = map.getContainer()
        if (m) {
          m.scrollIntoView()
          m.classList.add('fullscreen')
          setFullscreen(true)
          disableScrolling()
        }

        maxBoundsFullscreen &&
          map.setMaxBounds(maxBoundsFullscreen as mapboxgl.LngLatBounds)

        map.resize()
      }
      if (onActivation) onActivation(map)
      setActive(true)
    }
    // Deactivate Interactions for Map
    map.deactivateMap = () => {
      map.touchZoomRotate.disable()
      map.dragRotate.disable()
      map.boxZoom.disable()
      map.scrollZoom.disable()
      map.dragPan.disable()
      map.keyboard.disable()
      map.doubleClickZoom.disable()

      if (browserCheck.isMobile) {
        const m = map.getContainer()
        if (m) m.classList.remove('fullscreen')

        enableScrolling()
        map.popup.remove()

        if (maxBounds) {
          map.setMaxBounds(maxBounds)
        }
        map.resize()
        map.flyTo({ center, zoom })
      }
      if (onDeactivation) onDeactivation(map)

      setActive(false)
    }
    map.deactivateMap()

    // Initial adjustments
    map.once('idle', () => {
      map.deactivateMap()
      if (browserCheck.isMobile) {
        const m = map.getContainer()
        if (m) m.classList.remove('fullscreen')
        map.setMaxBounds(maxBounds)
        map.resize()
      }
    })

    // Calling getMap-Function
    if (getMap) {
      getMap(map)
    }
  }, [])

  return (
    <>
      <div id={id} {...props} />
      {isActive && (
        <div className='keyContainer'>
          {isFullscreen && FullscreenMapKey && FullscreenMapKey}
        </div>
      )}
    </>
  )
}
